import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PasswordInput from '../../components/PasswordInput';
import Captcha from '../../components/Captcha';
import { useAuth } from '../../context/AuthContext';
import { resolveMedia, useSettings } from '../../context/SettingsContext';
import { DEFAULT_LOGO } from '../../config/brand';

export default function Register() {
  const { settings } = useSettings();
  const { register, loading } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [agreeTerms, setAgreeTerms] = useState(true);
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [errors, setErrors] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = 'Please enter your full name';
    }

    if (!form.email.trim()) {
      newErrors.email = 'Please enter your email address';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!form.password) {
      newErrors.password = 'Please create a password (min. 6 characters)';
    } else if (form.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!isCaptchaValid) {
      newErrors.captcha = 'Please enter the exact 5-character Captcha code';
    }

    if (!agreeTerms) {
      newErrors.agree = 'You must agree to the Terms of Service & Privacy Policy';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill in all highlighted required fields');
      return;
    }

    setErrors({});

    try {
      await register(form);
      toast.success('Welcome to Chandana Aura! Account created successfully.');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    }
  };

  return (
    <div className="min-h-[70vh] bg-[#FAF8F5] pt-3 pb-8 sm:pt-5 sm:pb-12 px-4 flex items-center justify-center text-[#2B1810]">
      <div className="w-full max-w-4xl bg-white border border-[#E8DFD8] rounded-2xl shadow-xl overflow-hidden grid md:grid-cols-12">
        
        {/* Left Visual Brand Column with Generated Sandalwood Art */}
        <div className="hidden md:flex md:col-span-5 relative text-[#FAF8F5] p-6 sm:p-7 flex-col justify-between overflow-hidden min-h-[520px]">
          {/* High-res Sandalwood Deity Idol Image */}
          <img
            src="/assets/auth_register_banner.jpg"
            alt="Authentic Mysore Sandalwood Idols"
            className="absolute inset-0 w-full h-full object-cover object-center transform scale-105 transition-transform duration-1000 hover:scale-110"
          />
          {/* Luxury Gradient Dark Overlays for Ultra-Clear Text Contrast */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C100A] via-[#1C100A]/60 to-[#1C100A]/75 backdrop-blur-[1.5px]" />
          <div className="absolute inset-0 bg-[radial-gradient(#C49A45_1px,transparent_1px)] [background-size:20px_20px] opacity-15" />

          {/* Top Brand Link & Badges */}
          <div className="relative z-10 space-y-4">
            <Link to="/" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10 hover:border-[#C49A45] transition-all group" aria-label="Back to store">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#C49A45] group-hover:underline flex items-center gap-1.5">
                <span>←</span> Back to Store
              </span>
            </Link>

            <div className="space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#C49A45] bg-[#C49A45]/20 backdrop-blur-md px-2.5 py-0.5 rounded-full border border-[#C49A45]/50 inline-block shadow-sm">
                Join Chandana Aura
              </span>
              <h2 className="font-heading text-xl sm:text-2xl font-bold leading-snug text-white drop-shadow-md">
                Experience the Divine Touch of Real Sandalwood
              </h2>
              <p className="text-xs text-[#E5D7CC] leading-relaxed drop-shadow-sm font-medium">
                Create an account to unlock special member discounts, early access to limited deity idols, and seamless order tracking.
              </p>
            </div>
          </div>

          {/* Trust Highlights Bottom Panel */}
          <div className="relative z-10 space-y-2 pt-4 border-t border-white/15 bg-black/30 backdrop-blur-md p-3.5 rounded-xl border border-white/10 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-[#C49A45]/30 border border-[#C49A45] text-[#C49A45] flex items-center justify-center font-bold text-xs shrink-0 shadow-inner">
                🎁
              </div>
              <div className="text-xs">
                <p className="font-bold text-white leading-tight">Exclusive Welcome Offer</p>
                <p className="text-[10px] text-[#CFC3BA]">10% OFF on your first sacred order</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-[#C49A45]/30 border border-[#C49A45] text-[#C49A45] flex items-center justify-center font-bold text-[11px] shrink-0 shadow-inner">
                🚚
              </div>
              <div className="text-xs">
                <p className="font-bold text-white leading-tight">Insured Pan-India Delivery</p>
                <p className="text-[10px] text-[#CFC3BA]">Tamper-proof sacred packaging</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Form Column */}
        <div className="md:col-span-7 p-5 sm:p-7 flex flex-col justify-center">
          {/* Header */}
          <div className="text-center md:text-left mb-3.5">
            <Link to="/" className="inline-block md:hidden mb-2">
              <img
                src={resolveMedia(settings?.logo || DEFAULT_LOGO)}
                alt={settings?.siteName || 'Chandana Aura'}
                className="h-9 mx-auto object-contain"
              />
            </Link>
            <h1 className="font-heading text-2xl font-bold text-[#2B1810]">
              Create Your Account
            </h1>
            <p className="text-xs text-[#8A796C] mt-0.5">
              Join thousands of devotees receiving authentic sandalwood products
            </p>
          </div>

          {/* Quick Tab Switcher */}
          <div className="flex border-b border-[#E8DFD8] mb-3.5">
            <Link
              to="/login"
              className="pb-2 px-6 font-bold text-xs uppercase tracking-wider text-[#8A796C] hover:text-[#2B1810] border-b-2 border-transparent transition-colors"
            >
              Sign In
            </Link>
            <button
              type="button"
              className="pb-2 font-bold text-xs uppercase tracking-wider text-[#C49A45] border-b-2 border-[#C49A45]"
            >
              Create Account
            </button>
          </div>

          {/* Register Form with noValidate */}
          <form onSubmit={submit} noValidate className="space-y-3.5">
            <div>
              <label className="block text-xs font-bold text-[#2B1810] mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                autoComplete="name"
                placeholder="e.g. Rajesh Sharma"
                className={`w-full h-10 px-3.5 text-xs rounded-lg border transition-all font-medium ${
                  errors.name
                    ? 'border-red-500 bg-red-50/20 text-red-900 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                    : 'border-[#D9D0C8] bg-[#FAF8F5] text-[#2B1810] placeholder-[#A0938A] focus:bg-white focus:border-[#C49A45] focus:ring-1 focus:ring-[#C49A45]'
                } focus:outline-none`}
                value={form.name}
                onChange={(e) => {
                  setForm({ ...form, name: e.target.value });
                  if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
                }}
              />
              {errors.name && (
                <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                  <span>⚠</span> {errors.name}
                </p>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-[#2B1810] mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  className={`w-full h-10 px-3.5 text-xs rounded-lg border transition-all font-medium ${
                    errors.email
                      ? 'border-red-500 bg-red-50/20 text-red-900 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                      : 'border-[#D9D0C8] bg-[#FAF8F5] text-[#2B1810] placeholder-[#A0938A] focus:bg-white focus:border-[#C49A45] focus:ring-1 focus:ring-[#C49A45]'
                  } focus:outline-none`}
                  value={form.email}
                  onChange={(e) => {
                    setForm({ ...form, email: e.target.value });
                    if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
                  }}
                />
                {errors.email && (
                  <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                    <span>⚠</span> {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-[#2B1810] mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  autoComplete="tel"
                  placeholder="+91 98765 43210"
                  className="w-full h-10 px-3.5 text-xs rounded-lg border border-[#D9D0C8] bg-[#FAF8F5] text-[#2B1810] placeholder-[#A0938A] focus:bg-white focus:border-[#C49A45] focus:ring-1 focus:ring-[#C49A45] focus:outline-none transition-all font-medium"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#2B1810] mb-1">
                Create Password <span className="text-red-500">*</span> <span className="text-[10px] text-[#8A796C] font-normal">(min. 6 characters)</span>
              </label>
              <PasswordInput
                autoComplete="new-password"
                placeholder="Create a strong password"
                value={form.password}
                error={Boolean(errors.password)}
                onChange={(e) => {
                  setForm({ ...form, password: e.target.value });
                  if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
                }}
              />
              {errors.password && (
                <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                  <span>⚠</span> {errors.password}
                </p>
              )}
            </div>

            {/* Captcha Security Check */}
            <div className="pt-1">
              <Captcha
                error={Boolean(errors.captcha)}
                onValidate={(valid) => {
                  setIsCaptchaValid(valid);
                  if (valid && errors.captcha) setErrors((prev) => ({ ...prev, captcha: '' }));
                }}
              />
              {errors.captcha && (
                <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                  <span>⚠</span> {errors.captcha}
                </p>
              )}
            </div>

            {/* Terms Agreement Checkbox */}
            <div className="pt-1">
              <div>
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => {
                      setAgreeTerms(e.target.checked);
                      if (e.target.checked && errors.agree) setErrors((prev) => ({ ...prev, agree: '' }));
                    }}
                    className={`w-4 h-4 mt-0.5 rounded border text-[#C49A45] focus:ring-[#C49A45] cursor-pointer accent-[#C49A45] ${
                      errors.agree ? 'border-red-500 ring-1 ring-red-500' : 'border-[#D9D0C8]'
                    }`}
                  />
                  <span className="text-xs text-[#54453D]">
                    I agree to Chandana Aura's{' '}
                    <Link to="/terms" target="_blank" className="font-bold text-[#C49A45] hover:underline">
                      Terms of Service
                    </Link>{' '}
                    and acknowledge the{' '}
                    <Link to="/privacy-policy" target="_blank" className="font-bold text-[#C49A45] hover:underline">
                      Privacy Policy
                    </Link>.
                  </span>
                </label>
                {errors.agree && (
                  <p className="text-[11px] font-semibold text-red-600 mt-1 pl-6 flex items-center gap-1">
                    <span>⚠</span> {errors.agree}
                  </p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#2B1810] hover:bg-[#C49A45] text-white font-bold text-xs uppercase tracking-widest rounded-lg shadow-md hover:shadow-lg transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <span>CREATING ACCOUNT...</span>
              ) : (
                <>
                  <span>CREATE ACCOUNT</span>
                  <span>→</span>
                </>
              )}
            </button>
          </form>

          {/* Footer Link */}
          <p className="text-xs text-center text-[#8A796C] mt-6 pt-4 border-t border-[#E8DFD8]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#C49A45] font-bold hover:underline">
              Sign in to your account
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
}
